import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
export default function TheConstructionProcess() {
  const steps = [{
    number: "01",
    name: "Consultation & Planning",
    description: "We analyze your property requirements, site conditions, and project goals to define a precise construction scope.",
    subSteps: ["Site assessment", "Blueprint review", "Budget alignment"]
  }, {
    number: "02",
    name: "Project Kickoff",
    description: "Our team aligns with stakeholders on timelines, material selection, safety protocols, and communication channels.",
    subSteps: ["Kickoff meeting", "Permit acquisition", "Schedule finalization"]
  }, {
    number: "03",
    name: "Execution & Build",
    description: "Our skilled craftsmen execute the build with precision, adhering to strict quality standards and safety regulations.",
    subSteps: ["Structural framing", "Quality inspections", "Daily site updates"]
  }, {
    number: "04",
    name: "Handover & Maintenance",
    description: "We deliver your completed project with a final walkthrough and ongoing property management support.",
    subSteps: ["Final walkthrough", "Project sign-off", "Maintenance support"]
  }];
  const roles = ["Homeowners", "Property Managers", "Real Estate Developers", "Commercial Clients", "Architects", "Investors"];
  return <section data-section-id="202" id="the-construction-process" className="relative bg-background text-foreground py-24 md:py-36 px-4 sm:px-6 lg:px-12 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 1px 1px, var(--foreground) 1px, transparent 0)",
      backgroundSize: "32px 32px"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-20 md:mb-28">
          <div className="lg:pr-8">
            <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--secondary)] mb-6">
              § 01
            </div>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-semibold tracking-tight">
              How we build your vision
            </h2>
          </div>
          <div className="lg:pl-8 flex lg:items-end lg:justify-end">
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed lg:text-right max-w-xl">
              A disciplined, four-stage construction process designed to embed our 
              expertise alongside your vision, from initial site assessment through 
              final delivery and long-term property stewardship.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:flex items-stretch justify-between gap-2 xl:gap-4">
            {steps.map((step, i) => <div key={step.number} data-index={i} className="flex items-stretch flex-1">
                {}
                <div className="group relative flex-1 max-w-[280px] mx-auto">
                  <div className="relative bg-card border border-border group-hover:border-[color:var(--secondary)] transition-all duration-300 px-6 py-10 h-full min-h-[340px] flex flex-col group-hover:shadow-lg" style={{
                clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)"
              }}>
                    <div className="font-mono text-[40px] xl:text-[48px] leading-none font-light text-[color:var(--secondary)] mb-4 text-center">
                      {step.number}
                    </div>
                    <h3 className="font-default text-lg font-semibold text-center mb-3 px-4">
                      {step.name}
                    </h3>
                    <p className="text-sm text-muted-foreground text-center leading-relaxed px-2">
                      {step.description}
                    </p>

                    {}
                    <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 px-2">
                      <ul className="space-y-1.5">
                        {step.subSteps.map((sub, si) => <li key={si} data-index={si} className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--secondary)] text-center">
                            · {sub}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>

                {}
                {i < steps.length - 1 && <div className="flex items-center justify-center px-1 xl:px-2 shrink-0">
                    <div className="flex items-center">
                      <div className="h-px w-8 xl:w-12 bg-[color:var(--secondary)]/40" />
                      <ChevronRight className="size-5 text-[color:var(--secondary)] -ml-1" strokeWidth={1.5} />
                    </div>
                  </div>}
              </div>)}
          </div>

          {}
          <div className="lg:hidden flex flex-col items-center gap-4">
            {steps.map((step, i) => <div key={step.number} data-index={i} className="flex flex-col items-center w-full">
                <div className="group relative w-full max-w-[300px]">
                  <div className="relative bg-card border border-border group-hover:border-[color:var(--secondary)] transition-all duration-300 px-6 py-10 min-h-[320px] flex flex-col group-hover:shadow-lg" style={{
                clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)"
              }}>
                    <div className="font-mono text-[44px] leading-none font-light text-[color:var(--secondary)] mb-3 text-center">
                      {step.number}
                    </div>
                    <h3 className="font-default text-lg font-semibold text-center mb-3 px-4">
                      {step.name}
                    </h3>
                    <p className="text-sm text-muted-foreground text-center leading-relaxed px-2">
                      {step.description}
                    </p>
                    <ul className="mt-4 space-y-1.5 px-2">
                      {step.subSteps.map((sub, si) => <li key={si} data-index={si} className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--secondary)] text-center">
                          · {sub}
                        </li>)}
                    </ul>
                  </div>
                </div>

                {i < steps.length - 1 && <div className="flex flex-col items-center py-2">
                    <div className="w-px h-6 bg-[color:var(--secondary)]/40" />
                    <ChevronDown className="size-5 text-[color:var(--secondary)] -mt-1" strokeWidth={1.5} />
                  </div>}
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-24">
          <div className="bg-secondary/10 border border-border px-6 py-6 md:px-10 md:py-7 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
              <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--secondary)] shrink-0 flex items-center gap-2">
                <ArrowRight className="size-3.5" strokeWidth={2} />
                OUR PARTNERS
              </div>
              <div className="flex flex-wrap gap-x-2 gap-y-2 md:gap-x-3">
                {roles.map((role, i) => <span key={role} data-index={i} className="inline-flex items-center px-3 py-1.5 text-xs md:text-sm font-mono text-foreground bg-background border border-border rounded-md">
                    {role}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}